// Kuvauspäivämäärä-suodatuksen UI: kaksi date+time-paria (alkaen/saakka)
// erillisillä date- ja time-input-kentillä (selain ei valitse osia eri
// tavoilla — type=datetime-local renderöi epäjohdonmukaisesti).
//
// Quick-presetit yleisille aikaikkunoille (Tänään, Viime 7pv, Viime 30pv,
// Tämä vuosi) jotka asettavat from + to atomisesti.

export type DateTimeRangePickerProps = {
  /** ISO-datetime alku (esim "2026-04-01T00:00:00Z") tai undefined. */
  from?: string;
  /** ISO-datetime loppu. */
  to?: string;
  /** Kutsutaan kun jompi kumpi muuttuu. Tyhjä string = tyhjenne. */
  onChange: (next: { from?: string; to?: string }) => void;
};

const labelClass = "block text-xs font-medium uppercase tracking-wide text-slate-400";
const inputClass =
  "w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 focus:border-slate-400 focus:outline-none";
const presetClass =
  "rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:bg-slate-800";

export function DateTimeRangePicker({ from, to, onChange }: DateTimeRangePickerProps) {
  const fromParts = isoToParts(from);
  const toParts = isoToParts(to);

  function emitFrom(date: string, time: string) {
    const iso = partsToIso(date, time, "start");
    onChange({ from: iso, ...(to ? { to } : {}) });
  }
  function emitTo(date: string, time: string) {
    const iso = partsToIso(date, time, "end");
    onChange({ ...(from ? { from } : {}), to: iso });
  }

  function applyPreset(start: Date, end: Date) {
    onChange({ from: start.toISOString(), to: end.toISOString() });
  }

  return (
    <div className="space-y-3">
      <fieldset className="grid grid-cols-2 gap-2">
        <legend className={labelClass}>Kuvattu — alkaen</legend>
        <input
          type="date"
          aria-label="Alkupäivä"
          value={fromParts.date}
          onChange={(e) => emitFrom(e.target.value, fromParts.time || "00:00")}
          className={inputClass}
        />
        <input
          type="time"
          aria-label="Alkukellonaika"
          value={fromParts.time}
          onChange={(e) => emitFrom(fromParts.date, e.target.value)}
          className={inputClass}
          disabled={fromParts.date === ""}
        />
      </fieldset>
      <fieldset className="grid grid-cols-2 gap-2">
        <legend className={labelClass}>Kuvattu — saakka</legend>
        <input
          type="date"
          aria-label="Loppupäivä"
          value={toParts.date}
          onChange={(e) => emitTo(e.target.value, toParts.time || "23:59")}
          className={inputClass}
        />
        <input
          type="time"
          aria-label="Loppukellonaika"
          value={toParts.time}
          onChange={(e) => emitTo(toParts.date, e.target.value)}
          className={inputClass}
          disabled={toParts.date === ""}
        />
      </fieldset>
      <div>
        <p className={labelClass}>Nopeavalinnat</p>
        <div className="mt-1 flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => applyPreset(startOfToday(), endOfToday())}
            className={presetClass}
          >
            Tänään
          </button>
          <button
            type="button"
            onClick={() =>
              applyPreset(addDays(startOfToday(), -1), endOfDay(addDays(new Date(), -1)))
            }
            className={presetClass}
          >
            Eilen
          </button>
          <button
            type="button"
            onClick={() => applyPreset(addDays(startOfToday(), -7), endOfToday())}
            className={presetClass}
          >
            Viime 7 päivää
          </button>
          <button
            type="button"
            onClick={() => applyPreset(addDays(startOfToday(), -30), endOfToday())}
            className={presetClass}
          >
            Viime 30 päivää
          </button>
          <button
            type="button"
            onClick={() => applyPreset(startOfYear(new Date()), endOfToday())}
            className={presetClass}
          >
            Tämä vuosi
          </button>
          {(from || to) && (
            <button
              type="button"
              onClick={() => onChange({})}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-400 hover:border-slate-500 hover:text-slate-200"
            >
              Tyhjennä
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** ISO datetime → { date: "YYYY-MM-DD", time: "HH:MM" } käyttäjän aikavyöhykkeellä. */
export function isoToParts(iso?: string): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** "YYYY-MM-DD" + "HH:MM" → ISO datetime. Tyhjä date palauttaa "" (tyhjennys).
 *  edge = "start" → puuttuva aika = 00:00, "end" → puuttuva aika = 23:59.
 */
export function partsToIso(date: string, time: string, edge: "start" | "end"): string {
  if (!date) return "";
  const defaultTime = edge === "start" ? "00:00" : "23:59";
  const t = time || defaultTime;
  const d = new Date(`${date}T${t}`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}
function endOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(23, 59, 59, 999);
  return r;
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
}
